import type { ReactNodeArray } from 'react';
import { memo } from 'react';
import { Panel } from 'react-bootstrap';
import Loader from '../../../generic/Loader';
import { RouterLink } from '../../../generic/Link';
import Text from '../../../generic/Text';
import Tooltip from '../../../generic/Tooltip';
import { Action } from '../../../../../typings/permissions';
import { userHasPermissionForTeam } from '../../../../helpers/permissions';
import { plural } from '../../../../../shared/helpers';
import LabelPill from '../../../generic/Pill/LabelPill';
import Flex from '../../../generic/Flex';
import styles from './ProjectAffiliation.module.less';
import { Badge, BadgeColors, BadgeShapes, BadgeSizes } from '../../../generic';

export type ProjectAffiliationTeam = {
  id: number;
  name: string;
};

type TeamAffiliationProps = {
  teams: ProjectAffiliationTeam[];
  totalCount: number;
  isProject: boolean;
};

type ProjectAffiliationWrapperProps = TeamAffiliationProps & {
  isFetching: boolean;
  labels: string[];
};

//  Base path (+ helper) to the team edit page.
const TEAM_LINK_BASE = '/account/settings/organization/teams/';
const getTeamURL = (id: number): string => `${TEAM_LINK_BASE}${id}`;

//  Max number of teams to display.
const MAX_TEAMS_DISPLAYED = 3;

const getProjectOrReleaseGroup = (isProject: boolean): 'project' | 'release group' =>
  isProject ? 'project' : 'release group';

//  Generate the hidden teams message, with proper wording/grammar.
const getHiddenTeamsText = (teamsCount: number, isProject: boolean, showOther: boolean): string => {
  const teamsPluralized = plural(teamsCount, 'team');
  const hasPluralized = teamsCount === 1 ? 'has' : 'have';
  const projectOrReleaseGroup = getProjectOrReleaseGroup(isProject);
  const other = showOther ? ' other' : '';
  return `${teamsCount}${other} ${teamsPluralized} ${hasPluralized} access to this ${projectOrReleaseGroup}.`;
};

/**
 * Renders the team name, either wrapped in a link to the team edit page,
 * or just in a static span (depending on the user's permissions).
 *
 * @param {number} id - Team id.
 * @param {string} name - Team name.
 */
const TeamAffiliationLink = ({ id, name }: ProjectAffiliationTeam) =>
  userHasPermissionForTeam(Action.Edit, id) ? (
    <RouterLink key={id} to={getTeamURL(id)} className="block">
      {name}
    </RouterLink>
  ) : (
    <div key={id}>{name}</div>
  );

/**
 * Displays the "+N More" tooltip, if there are too many teams to display.
 *
 * @param {ProjectAffiliationTeam[]} restOfTeams - The rest of the teams.
 */
const TeamAffiliationRest = ({ restOfTeams }: { restOfTeams: ProjectAffiliationTeam[] }) => (
  <Tooltip
    tooltip={restOfTeams.reduce<ReactNodeArray>(
      (tooltip, team) => tooltip.concat(<div key={team.id}>{team.name}</div>),
      [],
    )}
  >
    <span className="inline-block">+ {restOfTeams.length} More</span>
  </Tooltip>
);

/**
 * Presentational component which displays the list of teams which the project/release group belongs to.
 *
 * @param {number} totalCount - The total count of teams the project/group belongs to, including ones the user can't see.
 * @param {ProjectAffiliationTeam[]} teams - The list of teams the user is allowed to view.
 * @param {boolean} isProject - If it is a project or release group.
 */
const TeamAffiliation = ({ totalCount, teams, isProject }: TeamAffiliationProps) => {
  if (totalCount === 0) {
    return <Text.Paragraph>No teams affiliated with this {getProjectOrReleaseGroup(isProject)}.</Text.Paragraph>;
  }
  //  The difference in these numbers is the teams the user is not "allowed to see".
  const hiddenTeamsCount = totalCount - teams.length;
  const showOther = hiddenTeamsCount > 0;

  //  For cases where the user is allowed to see everything (ie: no hidden teams)
  if (hiddenTeamsCount === 0) {
    return teams.length > 0 ? (
      <>
        {teams.slice(0, MAX_TEAMS_DISPLAYED).map(TeamAffiliationLink)}
        {teams.length > MAX_TEAMS_DISPLAYED && <TeamAffiliationRest restOfTeams={teams.slice(MAX_TEAMS_DISPLAYED)} />}
      </>
    ) : (
      <Text.Paragraph>{getHiddenTeamsText(hiddenTeamsCount, isProject, showOther)}</Text.Paragraph>
    );
  }
  //  Otherwise, the user is able to see only _some_ of the teams.
  return (
    <>
      {teams.map(TeamAffiliationLink)}
      <Text.Paragraph>and {getHiddenTeamsText(hiddenTeamsCount, isProject, showOther)}</Text.Paragraph>
    </>
  );
};

const ProjectLabels = (labels: string[] = []) => {
  return (
    <>
      <div className={styles.header}>
        <span className={styles.label}>Labels</span>
        {
          labels.length > 0 && (
            <Badge
              key="label-count"
              color={BadgeColors.LightGray}
              shape={BadgeShapes.Circle}
              size={BadgeSizes.Micro}
            >
              {labels.length}
            </Badge>
          )
        }
      </div>
      <Flex wrap="wrap" justify="flex-start">
        {labels.length > 0 ? (
          labels?.map((l) => <LabelPill label={l} />)
        ) : (
          <Text.Paragraph> This project has no labels. Go to settings to add them.</Text.Paragraph>
        )}
      </Flex>
    </>
  );
};

/**
 * "Wrapper" for the TeamAffiliation component, which handles rendering
 * the Panel, Header, and Loader.
 *
 * @param {boolean} isFetching - If the teams are currently being fetched.
 * @param {number} totalCount - The total count of teams the project/group belongs to, including ones the user can't see.
 * @param {ProjectAffiliationTeam[]} teams - The list of teams the user is allowed to view.
 * @param {boolean} isProject - If it is a project or release group.
 */
const ProjectAffiliationWrapper = ({
  teams,
  totalCount,
  isFetching,
  isProject,
  labels,
}: ProjectAffiliationWrapperProps) => (
  <>
    <Panel className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.label}>Team Access</span>
        {!isFetching && totalCount > 0 && (
          <Badge
            key="team-access-count"
            color={BadgeColors.LightGray}
            shape={BadgeShapes.Circle}
            size={BadgeSizes.Micro}
          >
            {totalCount}
          </Badge>
        )}
      </div>
      {isFetching ? <Loader /> : <TeamAffiliation teams={teams} isProject={isProject} totalCount={totalCount} />}
    </Panel>
    {isProject && <Panel className={styles.panel}>{ProjectLabels(labels)}</Panel>}
  </>
);

export default memo(ProjectAffiliationWrapper);
